/**
 * Support access gate for the user surface. Admins always pass; non-admins must
 * have a row in `user_support_access`. Mirrors the HRGuard / CommunicationGuard
 * pattern (backed by GET /support/access). Previously `/support` mounted with no
 * guard even though every sibling module has one (audit ARCH-10).
 */
import { useQuery } from '@tanstack/react-query';
import { Loader2, ShieldX } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { apiRequest } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';

export function SupportGuard({ children }: { children: React.ReactNode }): JSX.Element {
  const { user } = useAuth();
  const accessQuery = useQuery({
    queryKey: queryKeys.support.access(user?.id ?? ''),
    queryFn: () => apiRequest<{ access: boolean }>('/support/access'),
    enabled: !!user,
    staleTime: 60_000,
  });

  if (accessQuery.isLoading) {
    return (
      <div className="grid h-full place-items-center text-ink-muted">
        <Loader2 className="size-5 animate-spin" />
      </div>
    );
  }
  if (!accessQuery.data?.access) {
    return (
      <div className="grid h-full place-items-center px-6">
        <div className="max-w-md text-center">
          <ShieldX className="mx-auto size-8 text-ink-muted" />
          <h2 className="mt-3 text-base font-semibold text-ink">No Support access</h2>
          <p className="mt-1 text-sm text-ink-muted">
            An administrator can grant you Support access from the user detail page.
          </p>
          <Link
            to="/"
            className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-accent hover:underline"
          >
            Back to portal
          </Link>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
